import { useMemo, useRef, useEffect } from 'react';
import { isEmpty } from 'lodash';
import { useTranslation } from 'react-i18next';
import { CUSTOM_OBJECTS } from 'queries/query-keys';
import { useQueries } from '@tanstack/react-query';
import { useDefaultActivityRelationshipsQuery } from 'queries/models/activites';
import CustomObjectsService from 'services/CustomObjectsService';
import { mapColumnSettings } from '__components/Charts/ScheduledActivities/helpers.js';
import { mapRelatedColumns } from '__components/Charts/ScheduledActivities/helpers';
import Loader from '__components/Kizen/Loader';
import { ResizableSection } from '__components/Wizards/CustomObject/steps/CustomLayout/dialogs/common';
import { ScheduledActivitiesColumnBuilder } from '__components/Wizards/Dashlet/sections/ValuesAndConstraints/reportTypes/ScheduledActivitiesColumnBuilder';
import { defaultColumnSettings } from '__components/Charts/ScheduledActivities/constants';
type CustomObjects = { id: string; fields: Array<{ id: string }> };
type Field = { id: string };
type ColumnSettings = {
  id: string;
  label: string;
  visible: boolean;
  disabled: boolean;
  sortable: boolean;
  fieldId: string;
  objectId: string;
  displayName: string;
};
type Metadata = {
  columns: ColumnSettings[];
  sort?: {
    columm: string;
    direction: string;
  };
};
type SetMetadataArgument = (metadata: Metadata, setDirty?: boolean) => void;

type TeamsActivitiesWizardProps = {
  activityFields?: any;
  noStepNumber?: boolean;
  setMetadata: SetMetadataArgument;
  metadata: Metadata;
  loading: boolean;
  isEdit: boolean;
  defaultColumnSettingsPreset?: any;
  disabledNameField: boolean;
  isGropedOptions: boolean;
  isCustomFieldItemAllowed?: boolean;
};

export const TeamsActivitiesWizard = (props: TeamsActivitiesWizardProps) => {
  const {
    activityFields = [],
    setMetadata,
    metadata,
    loading,
    isEdit,
    defaultColumnSettingsPreset = defaultColumnSettings,
    disabledNameField = true,
    isGropedOptions = false,
  } = props;
  const initialRenderRef = useRef(true);
  const { t } = useTranslation();

  useEffect(() => {
    if (!initialRenderRef.current) {
      initialRenderRef.current = true;
    } else {
      initialRenderRef.current = false;
    }
  }, []);

  const defaultActivityRelationshipsQuery =
    useDefaultActivityRelationshipsQuery();

  const { relatedObjects = null } = useMemo(() => {
    if (defaultActivityRelationshipsQuery.isLoading) {
      return {};
    }
    const doa = defaultActivityRelationshipsQuery.data;
    const relatedObjects = mapRelatedColumns(doa);
    if (activityFields.length) {
      return {
        relatedObjects: [
          {
            label: t('Associated Objects'),
            id: 'relatedObjects',
            isGroupedItem: true,
          },
          ...relatedObjects,
          {
            label: t('Activity Fields'),
            id: 'activityFields',
            isGroupedItem: true,
          },
          ...activityFields.map((field: any) => ({
            id: field.id,
            label: field.displayName,
            disabled: field.is_hidden,
            order: field.order,
            description: field.description,
            description_visibility: field.descriptionVisibility,
            related: false,
            visible: false,
          })),
        ],
      };
    }
    return {
      relatedObjects: relatedObjects,
    };
  }, [
    activityFields,
    defaultActivityRelationshipsQuery.data,
    defaultActivityRelationshipsQuery.isLoading,
    t,
  ]);

  const columnSettings = useMemo(() => {
    if (metadata) {
      const savedColumns = metadata.columns ?? [];
      return mapColumnSettings(
        savedColumns,
        relatedObjects,
        t,
        defaultColumnSettingsPreset
      );
    }
    return mapColumnSettings(
      [],
      relatedObjects,
      t,
      defaultColumnSettingsPreset
    );
  }, [metadata, relatedObjects, t, defaultColumnSettingsPreset]);

  const columnAssociatedObjectFieldIds = useMemo(() => {
    return columnSettings.columns
      .filter(({ fieldId }: ColumnSettings) => fieldId)
      .map(({ fieldId, objectId }: ColumnSettings) => ({
        fieldId,
        objectId,
      }));
  }, [columnSettings]);

  const objectQueries = useQueries({
    queries: columnAssociatedObjectFieldIds.map(({ objectId }) => ({
      queryKey: CUSTOM_OBJECTS.DETAILS(objectId),
      queryFn: () =>
        CustomObjectsService.getCustomObjectDetails(objectId).catch(() => ({
          id: 'not_found',
          fields: [],
          name: '',
          description: '',
        })),
      enabled: Boolean(objectId),
    })),
  });

  const objectsById = useMemo(() => {
    const result: { [key: string]: { fieldsById: any } } = {};
    for (const query of objectQueries) {
      if (query.data && query.data.id !== 'not_found') {
        const data: CustomObjects = query.data;
        result[data.id] = {
          ...data,
          fieldsById: data.fields.reduce(
            (acc: { [key: string]: Field }, field: Field) => {
              acc[field.id] = field;
              return acc;
            },
            {}
          ),
        };
      }
    }
    return result;
  }, [objectQueries]);

  const isObjectsByIdLoading = useMemo(
    () => objectQueries.some((q) => q.isLoading),
    [objectQueries]
  );

  const filteredColumnSettings = useMemo(() => {
    if (isEmpty(objectsById)) {
      return columnSettings;
    }
    const columnFilter = ({ fieldId, objectId, visible }: ColumnSettings) =>
      !fieldId || objectsById[objectId]?.fieldsById[fieldId] || !visible;
    const activeColumns = columnSettings.columns.filter(columnFilter);
    return {
      ...columnSettings,
      columns: activeColumns,
    };
  }, [columnSettings, objectsById]);
  return (
    <ResizableSection header={t('Choose Activity Columns')} {...props}>
      {loading ||
      defaultActivityRelationshipsQuery.isLoading ||
      isObjectsByIdLoading ||
      !filteredColumnSettings.columns ? (
        <Loader style={{ width: '100%', height: '200px' }} loading />
      ) : (
        <ScheduledActivitiesColumnBuilder
          scrollActvitiyBuilderIntoView={initialRenderRef.current}
          defaultColumns={defaultColumnSettingsPreset || defaultColumnSettings}
          columns={filteredColumnSettings.columns}
          scheduledActivitiesConfig={{}}
          displayHeader={false}
          onChange={setMetadata}
          displayDefaultColumns={!isEdit}
          disabledNameField={disabledNameField}
          isGropedOptions={isGropedOptions}
        />
      )}
    </ResizableSection>
  );
};
